import { useRef } from 'react';
import { CalendarRange } from 'lucide-react';
import { dateRange, longDate, mondayOf, sundayOf, type ISODate } from '@core/dates';
import { SegmentedControl } from '@core/ui/SegmentedControl';
import { control } from '@core/ui/form';
import { ToolbarDropdown, menuItem } from './Toolbar';
import { MONTH_PRESETS, defaultRange, type Preset, type Range } from './range';

export type { Range } from './range';

/**
 * Видимый период одним контролом: «3 Aug 2026 – 31 Jan 2027 · Half year ▾», внутри пресеты длины,
 * даты и сброс. onReset — снять личную настройку: диапазон снова считается от сегодняшнего дня при
 * каждом открытии, а не хранится.
 */
export function RangeControls({ range, onChange, onReset, presets = MONTH_PRESETS, def = defaultRange() }: { range: Range; onChange: (r: Range) => void; onReset: () => void; presets?: Preset[]; def?: Range }) {
  const isDefault = range.from === def.from && range.to === def.to;
  const preset = presets.find((p) => range.to === p.to(range.from));
  // Выбранная в календаре дата раздвигается до своей недели: сетка без обрезанных недель.
  const set = (from: ISODate, to: ISODate) => { if (from && to && from <= to) onChange({ from: mondayOf(from), to: sundayOf(to) }); };
  const row = 'flex items-center gap-2 px-3 py-1.5';
  return (
    <ToolbarDropdown
      role="group" dataAttr="data-range-menu" icon={<CalendarRange className="w-4 h-4" />} title="Visible period (only for you)"
      label={<span className="text-gray-800"><span className="tabular-nums">{dateRange(range.from, range.to)}</span>{preset && <span className="text-gray-500"> · {preset.label}</span>}</span>}
    >
      <div className={row}>
        <SegmentedControl
          role="group" label="Range length"
          items={presets.map((p) => ({ key: p.label, label: p.label, title: p.title }))}
          value={preset?.label ?? null}
          onChange={(label) => onChange({ from: range.from, to: presets.find((p) => p.label === label)!.to(range.from) })}
        />
      </div>
      <div className="my-1 h-px bg-gray-200" />
      <div className={row}><span className="w-10 text-gray-500">From</span><DateButton value={range.from} max={range.to} onChange={(v) => set(v, range.to)} label="From" /></div>
      <div className={row}><span className="w-10 text-gray-500">To</span><DateButton value={range.to} min={range.from} onChange={(v) => set(range.from, v)} label="To" /></div>
      <div className="my-1 h-px bg-gray-200" />
      <button type="button" data-range-reset className={menuItem} onClick={onReset} disabled={isDefault} title="Back to the default period — it follows today again">Reset to default</button>
    </ToolbarDropdown>
  );
}

// Дата как текст в одном формате для всех; нативный календарь открывается по клику.
// Сам <input type="date"> спрятан: его формат зависит от языка браузера, а не страницы.
export function DateButton({ value, min, max, onChange, label }: { value: ISODate; min?: ISODate; max?: ISODate; onChange: (v: ISODate) => void; label: string }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <span className="relative inline-flex">
      <button
        type="button" aria-label={label} title={label}
        className={`${control} hover:bg-gray-50 tabular-nums`}
        onClick={() => ref.current?.showPicker()}
      >
        {longDate(value)}
      </button>
      <input
        ref={ref} type="date" tabIndex={-1} aria-hidden value={value} min={min} max={max}
        className="absolute left-0 bottom-0 w-px h-px opacity-0 pointer-events-none"
        onChange={(e) => { if (e.target.value) onChange(e.target.value); }}
      />
    </span>
  );
}
